import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { pagosApi, mediosPagoApi } from '../api/pagos'
import { notasVentaApi } from '../api/notasVenta'
import BuscadorCliente from '../components/BuscadorCliente'
import ModalAltaCliente from '../components/ModalAltaCliente'
import ModalReciboPresea from '../components/ModalReciboPresea'
import SelectorBancoPresea from '../components/SelectorBancoPresea'
import { TIPOVALOR } from '../api/bancos'
import { fmtFecha, fmtMonto, parseMonto, hoyISO } from '../utils/fecha'
import { etiquetaCliente } from '../utils/cliente'
import Boton from '../components/Boton'
import MoneyInput from '../components/MoneyInput'
import useDirtyGuard from '../hooks/useDirtyGuard'
import CampoFecha from '../components/CampoFecha'

const FILA_VACIA = () => ({ _key: Math.random(), medio_pago_id: '', importe: '', id_transaccion: '', cod_autorizacion: '', nro_cupon_cheque: '', cuotas: 1, lote: '', fecha_pago: '', n_bco: '', n_bco_nombre: '' })

const CAMPOS_POR_TIPO = {
  tarjeta:      ['nro_cupon_cheque', 'cod_autorizacion', 'id_transaccion', 'cuotas', 'lote'],
  cheque:       ['nro_cupon_cheque'],
  transferencia:['id_transaccion'],
  mp:           ['id_transaccion', 'cod_autorizacion'],
  efectivo:     [],
}

// Tipovalor de BANCOS Presea según el tipo del MedioPago seleccionado.
// - tarjeta y MP (pasarela) → tipovalor 4 (TARJETA/pasarela)
// - cheque → 1 ; efectivo → 7 ; transferencia → 8
const TIPOVALOR_POR_TIPO = {
  tarjeta:       TIPOVALOR.TARJETA,
  mp:            TIPOVALOR.TARJETA,
  cheque:        TIPOVALOR.CHEQUE,
  efectivo:      TIPOVALOR.EFECTIVO,
  transferencia: TIPOVALOR.TRANSFERENCIA,
}

// Para estos tipos el N_BCO no se puede sacar del codigo_presea genérico del
// MedioPago — Presea rebota con ERR13 si no es un código real del maestro.
// Acá el SelectorBancoPresea es OBLIGATORIO.
const TIPOS_REQUIEREN_BANCO = new Set(['tarjeta', 'cheque'])
// Sugerencia #78 (Diego, 17 sep 2026): cada medio de pago con su propia fecha.
const FECHA_POR_MEDIO = { cheque: 'Fecha de pago del cheque', transferencia: 'Fecha de la transferencia', tarjeta: 'Fecha del cupón', mp: 'Fecha de la operación' }
// Para estos otros, el codigo_presea histórico anda (CLAUDE.md confirmado),
// pero igual ofrecemos el selector por si el operador quiere especificar
// (ej. transferencia BBVA vs genérica). Efectivo NO se muestra: va
// siempre EFECTIVO PESOS (n_bco=999) sin preguntar (regla Martin 19 jun).
const TIPOS_PERMITEN_BANCO   = new Set(['tarjeta', 'cheque', 'transferencia', 'mp'])
// N_BCO fijo cuando el operador no elige uno explícito. Hoy solo aplica a
// efectivo (siempre EFECTIVO PESOS).
const N_BCO_AUTO_POR_TIPO = { efectivo: 999 }

export default function PagoFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const esEdicion = Boolean(id)

  const [cliente, setCliente] = useState(null)
  const [fecha, setFecha] = useState(hoyISO())   // fija: siempre hoy
  const [observaciones, setObservaciones] = useState('')
  const [filas, setFilas] = useState([FILA_VACIA()])
  const [medios, setMedios] = useState([])
  const [cargando, setCargando] = useState(esEdicion)
  const [saving, setSaving] = useState(false)
  const [errores, setErrores] = useState({})
  const [mensajeOk, setMensajeOk] = useState('')
  const [showAltaCliente, setShowAltaCliente] = useState(false)
  const [showReciboPresea, setShowReciboPresea] = useState(false)

  // Cobranza a medio cargar no se pierde con F5 / click al sidebar.
  useDirtyGuard(!saving && !mensajeOk && (!!cliente || filas.some(f => f.importe || f.medio_pago_id)))

  // Facturas pendientes del cliente
  const [nvsPendientes, setNvsPendientes] = useState([])
  const [nvSeleccionadas, setNvSeleccionadas] = useState([])
  const [montosNv, setMontosNv] = useState({})   // { [nvId]: 'monto imputado' }
  const [montoPrevio, setMontoPrevio] = useState({})  // { [nvId]: valor antes de tildar "saldo completo" }
  const [importeManual, setImporteManual] = useState(false)  // el usuario tipeó el importe a mano

  // Saldo pendiente real del comprobante (total − ya imputado en recibos previos).
  // Fallback al total si el backend todavía no manda saldo.
  const saldoDe = (nv) => Number(nv.saldo ?? nv.total) || 0
  // Solo comprobantes con saldo pendiente: los saldados no se ofrecen
  const nvsConSaldo = nvsPendientes.filter(nv => saldoDe(nv) > 0.005)

  // Total imputado = suma de los montos cargados por comprobante seleccionado
  const totalSeleccionado = nvSeleccionadas
    .reduce((acc, id) => acc + (parseMonto(montosNv[id]) || 0), 0)

  useEffect(() => {
    mediosPagoApi.listar(true).then(({ data }) => setMedios(data)).catch(() => {})
  }, [])

  useEffect(() => {
    if (!esEdicion) return
    pagosApi.obtener(id).then(({ data }) => {
      setCliente(data.cliente)
      // Al editar, la fecha defaultea a HOY (no a la fecha original del pago)
      // — pedido del usuario 24/06/26: si está corrigiendo un pago, lo más
      // probable es que la fecha también deba actualizarse al día de edición.
      setFecha(hoyISO())
      setObservaciones(data.observaciones || '')
      // La forma de pago guardada vive en pago_valores (marca/N_BCO real,
      // cupón, aut., lote). Sin precargarla, el form abría con la marca vacía
      // y parecía que la edición anterior no se había grabado (bug 7 ago).
      const v = data.valores?.[0]
      setFilas([{
        _key: Math.random(),
        medio_pago_id: String(data.medio_pago_id),
        importe: data.importe,
        id_transaccion: v?.id_transaccion ?? data.id_transaccion ?? '',
        cod_autorizacion: v?.cod_autorizacion ?? data.cod_autorizacion ?? '',
        nro_cupon_cheque: v?.nro_cupon_cheque ?? data.nro_cupon_cheque ?? '',
        fecha_pago: v?.fecha_pago ? String(v.fecha_pago).slice(0, 10) : '',
        cuotas: v?.cuotas ?? data.cuotas ?? 1,
        lote: v?.lote ?? data.lote ?? '',
        n_bco: v?.n_bco ?? '',
        n_bco_nombre: v?.banco?.nombre ?? '',
      }])
      setCargando(false)
    }).catch(() => navigate('/pagos'))
  }, [id, esEdicion, navigate])

  // Cargar NVs procesadas del cliente cuando cambia
  useEffect(() => {
    if (!cliente?.id) { setNvsPendientes([]); setNvSeleccionadas([]); setMontosNv({}); return }
    notasVentaApi.listar({ cliente_id: cliente.id, estado: 'procesado', per_page: 20 })
      .then(({ data }) => setNvsPendientes(data.data || []))
      .catch(() => setNvsPendientes([]))
  }, [cliente?.id])

  // Por defecto, el importe de la cobranza SIGUE al total que imputás a los
  // comprobantes (lo normal: cobrás exactamente lo que imputás). Así poner el
  // monto en la factura ya completa la plata, sin doble carga. Si el usuario
  // edita el importe a mano deja de seguirlo. Solo con UNA forma de pago.
  useEffect(() => {
    if (importeManual || filas.length !== 1) return
    setFilas(prev => [{ ...prev[0], importe: totalSeleccionado ? totalSeleccionado.toFixed(2) : '' }])
  }, [totalSeleccionado, importeManual, filas.length])

  const setFila = (key, campo, valor) => {
    // editar el importe a mano lo desengancha del total imputado
    if (campo === 'importe') setImporteManual(true)
    setFilas(prev => prev.map(f => {
      if (f._key !== key) return f
      // Al cambiar de medio de pago, el N_BCO anterior puede ser inválido (era
      // de otro tipovalor) — lo limpiamos para forzar elegir de nuevo.
      if (campo === 'medio_pago_id') return { ...f, medio_pago_id: valor, n_bco: '', n_bco_nombre: '' }
      return { ...f, [campo]: valor }
    }))
  }
  // Recibe { n_bco, nombre } | null desde SelectorBancoPresea.
  const setBancoFila = (key, sel) => {
    setFilas(prev => prev.map(f => f._key === key
      ? { ...f, n_bco: sel?.n_bco ?? '', n_bco_nombre: sel?.nombre ?? '' }
      : f))
  }
  const agregarFila = () => setFilas(prev => [...prev, FILA_VACIA()])
  const quitarFila = (key) => setFilas(prev => prev.filter(f => f._key !== key))

  const total = filas.reduce((sum, f) => sum + (parseMonto(f.importe) || 0), 0)

  const toggleNv = (nvId) => {
    setImporteManual(false)   // al cambiar comprobantes, el importe vuelve a seguir el total imputado
    setNvSeleccionadas(prev => {
      if (prev.includes(nvId)) {
        setMontosNv(m => { const n = { ...m }; delete n[nvId]; return n })
        setMontoPrevio(p => { const n = { ...p }; delete n[nvId]; return n })
        return prev.filter(x => x !== nvId)
      }
      // al seleccionar, el monto arranca en el saldo pendiente del comprobante
      const nv = nvsPendientes.find(x => x.id === nvId)
      setMontosNv(m => ({ ...m, [nvId]: saldoDe(nv).toFixed(2) }))
      return [...prev, nvId]
    })
  }

  const setMontoNv = (nvId, valor) => {
    setImporteManual(false)   // al cambiar el monto imputado, el importe lo sigue
    setMontosNv(m => ({ ...m, [nvId]: valor }))
  }

  // ¿El monto imputado a este comprobante cubre todo su saldo? (tolerancia de centavos)
  const cubreSaldo = (nv) => {
    const saldo = saldoDe(nv)
    return saldo > 0 && Math.abs((parseMonto(montosNv[nv.id]) || 0) - saldo) < 0.005
  }

  // Tilde "saldo restante": al marcar, completa el monto con el saldo total del
  // comprobante (reversible — al destildar restaura el valor que había antes).
  const togglePagarSaldo = (nv) => {
    setImporteManual(false)
    if (cubreSaldo(nv)) {
      const previo = montoPrevio[nv.id] ?? ''
      setMontosNv(m => ({ ...m, [nv.id]: previo }))
    } else {
      setMontoPrevio(p => ({ ...p, [nv.id]: montosNv[nv.id] ?? '' }))
      setMontosNv(m => ({ ...m, [nv.id]: saldoDe(nv).toFixed(2) }))
    }
  }

  // Builder de valores[] para el payload — uno por fila, con N_BCO real si se
  // eligió. Backend lo guarda en pago_valores; ReciboPreseaService lo usa antes
  // que el codigo_presea legacy del MedioPago. Para tipos con N_BCO automático
  // (efectivo → 999) usamos el default cuando no hay elección explícita.
  const armarValoresDeFila = (f) => {
    const medio = medios.find(m => String(m.id) === String(f.medio_pago_id))
    if (!medio?.tipo) return undefined
    const nBcoFinal = f.n_bco || N_BCO_AUTO_POR_TIPO[medio.tipo]
    if (!nBcoFinal) return undefined
    return [{
      tipo_valor: medio.tipo,
      n_bco: parseInt(nBcoFinal),
      importe: parseMonto(f.importe),
      cuotas: parseInt(f.cuotas) || 1,
      nro_cupon_cheque: f.nro_cupon_cheque || null,
      cod_autorizacion: f.cod_autorizacion || null,
      id_transaccion: f.id_transaccion || null,
      lote: f.lote || null,
      // Sugerencia #78 (Diego): cada medio con su fecha — obligatoria en el
      // cheque, opcional en transferencia / tarjeta / billetera.
      fecha_pago: ['cheque', 'transferencia', 'tarjeta', 'mp'].includes(medio.tipo) ? (f.fecha_pago || null) : null,
    }]
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!cliente?.id) { setErrores({ cliente_id: ['Seleccioná un cliente.'] }); return }
    if (filas.some(f => !f.medio_pago_id || !f.importe)) {
      setErrores({ general: ['Completá medio de pago e importe en todas las filas.'] }); return
    }
    // Tarjeta y cheque OBLIGAN a elegir N_BCO real (sino Presea rebota con ERR13).
    const filaSinBanco = filas.find(f => {
      const medio = medios.find(m => String(m.id) === String(f.medio_pago_id))
      return TIPOS_REQUIEREN_BANCO.has(medio?.tipo) && !f.n_bco
    })
    if (filaSinBanco) {
      setErrores({ general: ['Para tarjeta y cheque tenés que elegir la marca/banco emisor de la lista de Presea (sino Presea rechaza el recibo).'] })
      return
    }
    // Cheque sin fecha de pago: sin este campo el operador la cargaba en la
    // fecha del recibo (pago #1129). El backend también la exige.
    const chequeSinFecha = filas.find(f => {
      const medio = medios.find(m => String(m.id) === String(f.medio_pago_id))
      return medio?.tipo === 'cheque' && !f.fecha_pago
    })
    if (chequeSinFecha) {
      setErrores({ general: ['Cargá la fecha de pago del cheque.'] })
      return
    }
    // Imputaciones con monto por comprobante (saldo parcial)
    const imputaciones = nvSeleccionadas.map(id => ({ nv_id: id, monto: parseMonto(montosNv[id]) || 0 }))
      .filter(i => i.monto > 0)
    setSaving(true)
    setErrores({})
    try {
      if (esEdicion) {
        const f = filas[0]
        const valores = armarValoresDeFila(f)
        await pagosApi.actualizar(id, {
          cliente_id: cliente.id, fecha, observaciones: observaciones || null,
          importe: parseMonto(f.importe), medio_pago_id: parseInt(f.medio_pago_id),
          id_transaccion: f.id_transaccion || null, cod_autorizacion: f.cod_autorizacion || null,
          nro_cupon_cheque: f.nro_cupon_cheque || null, cuotas: parseInt(f.cuotas) || 1,
          lote: f.lote || null,
          ...(valores ? { valores } : {}),
          ...(imputaciones.length ? { imputaciones } : {}),
        })
        setMensajeOk('Pago actualizado.')
        setTimeout(() => navigate(`/pagos/${id}`), 1200)
      } else {
        let primeroPago = null
        for (const f of filas) {
          const valores = armarValoresDeFila(f)
          const { data } = await pagosApi.crear({
            cliente_id: cliente.id, fecha, observaciones: observaciones || null,
            importe: parseMonto(f.importe), medio_pago_id: parseInt(f.medio_pago_id),
            id_transaccion: f.id_transaccion || null, cod_autorizacion: f.cod_autorizacion || null,
            nro_cupon_cheque: f.nro_cupon_cheque || null, cuotas: parseInt(f.cuotas) || 1,
            lote: f.lote || null,
            ...(valores ? { valores } : {}),
            // La imputación a comprobantes va solo en el 1er recibo de la cobranza
            // (en multi-medio, atarla a todos duplicaría el saldo cobrado).
            ...(!primeroPago && imputaciones.length ? { imputaciones } : {}),
          })
          if (!primeroPago) primeroPago = data
        }
        // Los pagos nacen PENDIENTES (no se encolan a Presea hasta el ✓ Confirmar
        // manual) — el mensaje anterior mentía y el paso de verificación quedaba invisible.
        setMensajeOk(`${filas.length > 1 ? filas.length + ' pagos registrados' : 'Pago registrado'} como PENDIENTE — confirmalo desde el listado cuando verifiques que la plata entró (banco / MP / Mobbex).`)
        setTimeout(() => navigate(`/pagos/${primeroPago.id}`), 1200)
      }
    } catch (err) {
      setErrores(err.response?.data?.errors || { general: [err.response?.data?.message || 'Error al guardar.'] })
    } finally { setSaving(false) }
  }

  if (cargando) return <p style={{ color: '#64748b' }}>Cargando...</p>

  return (
    <div style={st.wrap}>
      <div style={st.headerRow}>
        <h2 style={st.title}>{esEdicion ? 'Editar Pago' : 'Nueva Cobranza'}</h2>
        <Boton variante="ghost" onClick={() => navigate(-1)}>← Volver</Boton>
      </div>

      {mensajeOk && <div style={st.success}>{mensajeOk}</div>}
      {errores.general && <div style={st.errorBox}>{errores.general[0]}</div>}

      <form onSubmit={handleSubmit}>
        <div style={st.grid}>
          <div style={st.col}>
            {/* Cliente */}
            <div style={st.card}>
              <h3 style={st.cardTitle}>Cliente</h3>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <BuscadorCliente key={cliente?.id || 'no'} value={cliente} onChange={setCliente} />
                </div>
                <Boton variante="primary" type="button" style={{ flexShrink: 0 }} onClick={() => setShowAltaCliente(true)} title="Nuevo cliente">+</Boton>
              </div>
              {cliente && <div style={st.clienteInfo}><strong>{etiquetaCliente(cliente)}</strong> — {cliente.cuit}</div>}
              {errores.cliente_id && <span style={st.err}>{errores.cliente_id[0]}</span>}

              {/* Recibo hecho EN PRESEA (retenciones): mismo circuito que en la
                  NV, pero libre — el cobro entra acreditado a cuenta del
                  cliente y se imputa después (Martin, 7 sep 2026). */}
              {!esEdicion && (
                <div style={{ marginTop: '0.6rem' }}>
                  <Boton variante="ghost" sm type="button"
                    title="El recibo se hizo en Presea (p. ej. con retenciones): cargá su número y LGR lo trae como cobro acreditado a cuenta de este cliente"
                    onClick={() => setShowReciboPresea(true)}>
                    🧾 Recibo hecho en Presea…
                  </Boton>
                </div>
              )}

              {/* NVs pendientes — se muestra el SALDO real (total − ya cobrado) */}
              {nvsConSaldo.length > 0 && (
                <div style={{ marginTop: '0.875rem' }}>
                  <div style={st.nvsTitle}>Comprobantes con saldo (seleccioná los que cubre esta cobranza)</div>
                  {nvsConSaldo.map(nv => {
                    const saldo = saldoDe(nv)
                    // monto_a_cobrar = importe fiscal (con percepciones) si el snap
                    // de DETALLE_COMPRO bajó. Fallback al total interno.
                    const totalCobrar = Number(nv.monto_a_cobrar ?? nv.total) || 0
                    const parcial = saldo < totalCobrar - 0.005
                    const sel = nvSeleccionadas.includes(nv.id)
                    return (
                      <div key={nv.id} style={st.nvRow}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, cursor: 'pointer' }}>
                          <input type="checkbox" checked={sel} onChange={() => toggleNv(nv.id)} />
                          <span style={{ flex: 1 }}>
                            <strong>{nv.numero_presea || nv.numero_lgr}</strong>
                            <span style={st.nvFecha}>{fmtFecha(nv.fecha)}</span>
                          </span>
                          <span style={st.nvTotal} title={parcial ? `Total $${fmtMonto(totalCobrar)} — ya cobrado $${fmtMonto(totalCobrar - saldo)}` : 'Saldo a cobrar'}>
                            ${fmtMonto(saldo)}
                            {parcial && <span style={st.nvParcial}>de ${fmtMonto(totalCobrar)}</span>}
                          </span>
                        </label>
                        {sel && (
                          <div style={st.montoWrap}>
                            <label style={st.saldoTilde} title={`Imputar el saldo restante completo ($${fmtMonto(saldo)})`}>
                              <input type="checkbox" checked={cubreSaldo(nv)} onChange={() => togglePagarSaldo(nv)} />
                              <span>saldo restante</span>
                            </label>
                            <MoneyInput
                              style={st.montoInput}
                              value={montosNv[nv.id] ?? ''}
                              onChange={v => setMontoNv(nv.id, v)}
                              placeholder={saldo.toFixed(2)}
                              title="Monto a imputar a este comprobante"
                            />
                            <button
                              type="button"
                              style={st.restoBtn}
                              onClick={() => setMontoNv(nv.id, saldo.toFixed(2))}
                              title={`Completar con el saldo total ($${fmtMonto(saldo)})`}
                            >resto</button>
                          </div>
                        )}
                      </div>
                    )
                  })}

                  {nvSeleccionadas.length > 0 && (
                    <>
                      <div style={st.nvSubtotal}>
                        <span>Total a imputar</span>
                        <strong>${fmtMonto(totalSeleccionado)}</strong>
                      </div>
                      <div style={st.imputaHint}>
                        💡 El importe de la cobranza abajo se completa solo con este total. Para un pago parcial, bajá el monto del comprobante.
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Fecha y obs */}
            <div style={st.card}>
              <h3 style={st.cardTitle}>Datos del pago</h3>
              <div>
                <label style={st.label}>Fecha</label>
                {/* Siempre HOY y no se edita (Martin, 16 sep 2026 — pago #1129):
                    la fija el backend. La fecha de un cheque va en su renglón. */}
                <div style={{ ...st.input, background: '#f8fafc', color: '#475569' }}
                  title="La cobranza se registra con la fecha de hoy. Si es un cheque, su fecha de pago se carga en la forma de pago.">
                  {fmtFecha(fecha)}
                </div>
              </div>
              <div style={{ marginTop: '0.75rem' }}>
                <label style={st.label}>Observaciones</label>
                <textarea style={st.textarea} value={observaciones} onChange={e => setObservaciones(e.target.value)} rows={2} />
              </div>
            </div>

            {/* Formas de pago */}
            <div style={st.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ ...st.cardTitle, margin: 0 }}>Formas de pago</h3>
                {!esEdicion && <Boton variante="secondary" sm type="button" onClick={agregarFila}>+ Agregar</Boton>}
              </div>

              {filas.map((fila, idx) => {
                const medio = medios.find(m => String(m.id) === String(fila.medio_pago_id))
                const tipo = medio?.tipo || ''
                const campos = CAMPOS_POR_TIPO[tipo] || []
                const tipovalor = TIPOVALOR_POR_TIPO[tipo]
                const mostrarBanco = TIPOS_PERMITEN_BANCO.has(tipo)
                const bancoRequerido = TIPOS_REQUIEREN_BANCO.has(tipo)
                const labelBanco = tipo === 'tarjeta' ? 'Marca / Pasarela'
                  : tipo === 'cheque' ? 'Banco emisor del cheque'
                  : tipo === 'transferencia' ? 'Cuenta de destino'
                  : tipo === 'mp' ? 'Pasarela (MP/MODO/etc.)'
                  : 'Caja'
                return (
                  <div key={fila._key} style={st.filaCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.8125rem', fontWeight: '700', color: '#64748b' }}>Pago {idx + 1}</span>
                      {filas.length > 1 && <Boton variante="destructive" sm type="button" onClick={() => quitarFila(fila._key)}>✕</Boton>}
                    </div>
                    <div style={st.row2}>
                      <div>
                        <label style={st.label}>Medio *</label>
                        <select style={st.input} value={fila.medio_pago_id} onChange={e => setFila(fila._key, 'medio_pago_id', e.target.value)} required>
                          <option value="">— Seleccionar —</option>
                          {medios.map(m => <option key={m.id} value={m.id}>{m.descripcion}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={st.label}>Importe *</label>
                        {/* Negativo permitido: una línea de DEVOLUCIÓN parcial (ej.
                            reintegro Mobbex) convive con la línea positiva del cobro —
                            Presea emite un recibo por cada una (+ y −) y el neto queda
                            imputado. Verificado en productivo 8 ago 2026 (recibos
                            200050976 / 200050977, NV I8MAIE). */}
                        <MoneyInput style={st.input} value={fila.importe} onChange={v => setFila(fila._key, 'importe', v)} placeholder="0.00" required permitirNegativo
                          title="Admite negativo para registrar una devolución parcial (el recibo neto debe quedar positivo)" />
                      </div>
                    </div>
                    {mostrarBanco && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <label style={st.label}>
                          {labelBanco} {bancoRequerido ? '*' : <span style={{ color: '#94a3b8', fontWeight: 500 }}>(opcional)</span>}
                        </label>
                        <SelectorBancoPresea
                          tipo={tipovalor}
                          value={fila.n_bco}
                          onChange={sel => setBancoFila(fila._key, sel)}
                          required={bancoRequerido}
                          placeholder={bancoRequerido ? 'Elegí el código real de Presea…' : 'Opcional — sino usa el por defecto del medio'}
                        />
                      </div>
                    )}
                    {campos.includes('nro_cupon_cheque') && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <label style={st.label}>{tipo === 'cheque' ? 'Nº cheque' : 'Nº cupón'}</label>
                        <input style={st.input} value={fila.nro_cupon_cheque} onChange={e => setFila(fila._key, 'nro_cupon_cheque', e.target.value)} />
                      </div>
                    )}
                    {FECHA_POR_MEDIO[tipo] && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <label style={st.label}>
                          {FECHA_POR_MEDIO[tipo]} {tipo === 'cheque' ? '*' : <span style={{ color: '#94a3b8', fontWeight: 500 }}>(opcional)</span>}
                        </label>
                        <CampoFecha style={st.input} value={fila.fecha_pago} onChange={v => setFila(fila._key, 'fecha_pago', v)} required={tipo === 'cheque'} />
                      </div>
                    )}
                    {campos.includes('cod_autorizacion') && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <label style={st.label}>Código autorización</label>
                        <input style={st.input} value={fila.cod_autorizacion} onChange={e => setFila(fila._key, 'cod_autorizacion', e.target.value)} />
                      </div>
                    )}
                    {campos.includes('id_transaccion') && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <label style={st.label}>ID transacción</label>
                        <input style={st.input} value={fila.id_transaccion} onChange={e => setFila(fila._key, 'id_transaccion', e.target.value)} />
                      </div>
                    )}
                    {campos.includes('cuotas') && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <label style={st.label}>Cuotas</label>
                        <input style={st.input} type="text" inputMode="numeric" pattern="[0-9]*" value={fila.cuotas} onChange={e => setFila(fila._key, 'cuotas', e.target.value)} />
                      </div>
                    )}
                    {campos.includes('lote') && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <label style={st.label}>Lote</label>
                        <input style={st.input} value={fila.lote} onChange={e => setFila(fila._key, 'lote', e.target.value)} maxLength={50} placeholder="Nº de lote del cupón" />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Resumen lateral */}
          <div style={st.resumenCard}>
            <h3 style={st.cardTitle}>Resumen</h3>
            <div style={st.resumenRow}><span style={{ color: '#64748b' }}>Cliente</span><span style={{ fontWeight: 600, textAlign: 'right', fontSize: '0.875rem', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{etiquetaCliente(cliente)}</span></div>
            <div style={st.resumenRow}><span style={{ color: '#64748b' }}>Fecha</span><span>{fmtFecha(fecha)}</span></div>
            <div style={st.resumenRow}><span style={{ color: '#64748b' }}>Formas de pago</span><span>{filas.length}</span></div>
            {nvSeleccionadas.length > 0 && (
              <div style={st.resumenRow}><span style={{ color: '#64748b' }}>Comprobantes</span><span>{nvSeleccionadas.length} selec.</span></div>
            )}
            <div style={{ ...st.resumenRow, borderTop: '2px solid #1e293b', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
              <span style={{ fontWeight: 700 }}>TOTAL</span>
              <strong style={{ fontSize: '1.25rem', color: '#1e40af' }}>
                ${fmtMonto(total)}
              </strong>
            </div>
            <Boton variante="primary" type="submit" style={{ width: '100%' }} disabled={saving || !cliente?.id}>
              {saving ? 'Guardando...' : esEdicion ? '💾 Actualizar' : '💾 Registrar cobranza'}
            </Boton>
          </div>
        </div>
      </form>

      {showReciboPresea && (
        <ModalReciboPresea
          cliente={cliente}
          onClose={() => setShowReciboPresea(false)}
          onImportado={(pago) => {
            // El cobro ya existe, acreditado: se sigue en su detalle (ahí
            // está el modal de imputación para aplicarlo a comprobantes).
            navigate(`/pagos/${pago.id}`)
          }}
        />
      )}
      {showAltaCliente && (
        <ModalAltaCliente
          onClose={() => setShowAltaCliente(false)}
          onCreado={(c) => { setCliente(c); setShowAltaCliente(false) }}
        />
      )}
    </div>
  )
}

const st = {
  wrap: { maxWidth: '980px' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' },
  title: { margin: 0, fontSize: '1.25rem', color: '#1e293b' },
  success: { background: '#dcfce7', color: '#166534', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem', fontWeight: '600' },
  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 280px', gap: '1rem', alignItems: 'start' },
  col: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  card: { background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  cardTitle: { margin: '0 0 0.875rem', fontSize: '0.9375rem', fontWeight: '700', color: '#1e293b' },
  resumenCard: { background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', position: 'sticky', top: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  label: { display: 'block', fontWeight: '600', fontSize: '0.8125rem', color: '#374151', marginBottom: '0.25rem' },
  input: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9375rem', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box', resize: 'vertical' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.625rem' },
  clienteInfo: { background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', padding: '0.4rem 0.75rem', fontSize: '0.8125rem', color: '#0c4a6e', marginTop: '0.5rem' },
  err: { color: '#dc2626', fontSize: '0.75rem', display: 'block', marginTop: '2px' },
  resumenRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.875rem', gap: '0.5rem' },
  nvsTitle: { fontSize: '0.8125rem', fontWeight: '700', color: '#374151', marginBottom: '0.5rem' },
  nvRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.5rem', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8125rem', background: '#f8fafc', marginBottom: '4px' },
  nvFecha: { marginLeft: '0.5rem', color: '#94a3b8', fontSize: '0.75rem' },
  nvTotal: { fontWeight: '700', color: '#1e40af', fontSize: '0.8125rem', marginLeft: 'auto' },
  nvParcial: { marginLeft: '0.375rem', color: '#94a3b8', fontWeight: '500', fontSize: '0.7rem' },
  montoWrap: { display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0 },
  saldoTilde: { display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.7rem', fontWeight: 600, color: '#166534', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  montoInput: { width: '110px', padding: '0.375rem 0.5rem', border: '1px solid #93c5fd', borderRadius: '5px', fontSize: '0.8125rem', textAlign: 'right', boxSizing: 'border-box', flexShrink: 0 },
  restoBtn: { padding: '0.3rem 0.4rem', fontSize: '0.7rem', fontWeight: 700, color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '5px', cursor: 'pointer', flexShrink: 0 },
  nvSubtotal: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.5rem 0.25rem', fontSize: '0.8125rem', color: '#475569', borderTop: '1px solid #e2e8f0', marginTop: '0.375rem' },
  pagarCompletoRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: '600', color: '#166534', background: '#f0fdf4', border: '1px solid #86efac', marginTop: '0.25rem' },
  imputaHint: { padding: '0.5rem', borderRadius: '6px', fontSize: '0.75rem', color: '#475569', background: '#f1f5f9', border: '1px solid #e2e8f0', marginTop: '0.25rem', lineHeight: 1.4 },
  filaCard: { background: '#f8fafc', borderRadius: '6px', padding: '0.875rem', marginBottom: '0.625rem', border: '1px solid #e2e8f0' },
}
